import { create } from 'zustand';
import { persist } from 'zustand/middleware';

/**
 * Persistent Transcription Store
 * Stores transcriptions per room with automatic cleanup
 */
export const useTranscriptionStore = create(
  persist(
    (set, get) => ({
      // Store structure: { roomCode: { transcriptions: [], lastUpdated: timestamp } }
      rooms: {},

      addTranscription: (roomCode, transcription) => {
        set((state) => {
          const room = state.rooms[roomCode] || { transcriptions: [], lastUpdated: Date.now() };
          const uniqueId = transcription.id || `${transcription.userId}-${transcription.timestamp}-${transcription.transcript}`;
          const exists = room.transcriptions.some(t => t.id === uniqueId);

          if (exists) {
            console.log('[TranscriptionStore] Skipping duplicate transcription');
            return state;
          }
          const enhancedTranscription = {
            ...transcription,
            id: uniqueId,
            storedAt: Date.now(),
          };

          return {
            rooms: {
              ...state.rooms,
              [roomCode]: {
                transcriptions: [...room.transcriptions, enhancedTranscription],
                lastUpdated: Date.now(),
              },
            },
          };
        });
      },

      /**
       * Add multiple transcriptions at once
       */
      addTranscriptions: (roomCode, transcriptions) => {
        set((state) => {
          const room = state.rooms[roomCode] || { transcriptions: [], lastUpdated: Date.now() };

          const newTranscriptions = transcriptions
            .filter(t => {
              const uniqueId = t.id || `${t.userId}-${t.timestamp}-${t.transcript}`;
              return !room.transcriptions.some(existing => existing.id === uniqueId);
            })
            .map(t => ({
              ...t,
              id: t.id || `${t.userId}-${t.timestamp}-${t.transcript}`,
              storedAt: Date.now(),
            }));

          if (newTranscriptions.length === 0) {
            return state;
          }

          return {
            rooms: {
              ...state.rooms,
              [roomCode]: {
                transcriptions: [...room.transcriptions, ...newTranscriptions],
                lastUpdated: Date.now(),
              },
            },
          };
        });
      },

      /**
       * Get all transcriptions for a room
       */
      getTranscriptions: (roomCode) => {
        const room = get().rooms[roomCode];
        return room ? room.transcriptions : [];
      },

      /**
       * Get transcriptions count for a room
       */
      getTranscriptionCount: (roomCode) => {
        const room = get().rooms[roomCode];
        return room ? room.transcriptions.length : 0;
      },

      /**
       * Clear transcriptions for a specific room
       */
      clearRoom: (roomCode) => {
        set((state) => {
          const { [roomCode]: removed, ...remainingRooms } = state.rooms;
          return { rooms: remainingRooms };
        });
      },

      /**
       * Clear all transcriptions (all rooms)
       */
      clearAll: () => {
        set({ rooms: {} });
      },

      /**
       * Remove old transcriptions (older than specified days)
       */
      cleanup: (daysToKeep = 7) => {
        const cutoffTime = Date.now() - (daysToKeep * 24 * 60 * 60 * 1000);

        set((state) => {
          const cleanedRooms = {};

          Object.entries(state.rooms).forEach(([roomCode, room]) => {
            if (room.lastUpdated > cutoffTime) {
              const recentTranscriptions = room.transcriptions.filter(
                t => (t.storedAt || room.lastUpdated) > cutoffTime
              );

              if (recentTranscriptions.length > 0) {
                cleanedRooms[roomCode] = {
                  transcriptions: recentTranscriptions,
                  lastUpdated: room.lastUpdated,
                };
              }
            }
          });

          return { rooms: cleanedRooms };
        });
      },

      /**
       * Export transcriptions for a room
       */
      exportRoom: (roomCode, format = 'json') => {
        const room = get().rooms[roomCode];
        if (!room) return null;

        if (format === 'json') {
          return JSON.stringify(room.transcriptions, null, 2);
        }

        if (format === 'txt') {
          return room.transcriptions
            .map(t => {
              const time = new Date(t.timestamp).toLocaleString();
              const text = t.transcript;
              const translation = t.translation ? ` (${t.translation})` : '';
              return `[${time}] ${t.userName}: ${text}${translation}`;
            })
            .join('\n');
        }

        if (format === 'csv') {
          const headers = 'Timestamp,User,Transcript,Translation,Language,Confidence\n';
          const rows = room.transcriptions
            .map(t => {
              const timestamp = new Date(t.timestamp).toISOString();
              const user = t.userName || '';
              const transcript = `"${t.transcript.replace(/"/g, '""')}"`;
              const translation = t.translation ? `"${t.translation.replace(/"/g, '""')}"` : '';
              const language = t.language || '';
              const confidence = t.confidence || '';
              return `${timestamp},${user},${transcript},${translation},${language},${confidence}`;
            })
            .join('\n');
          return headers + rows;
        }

        return null;
      },

      /**
       * Search transcriptions in a room
       */
      searchRoom: (roomCode, query) => {
        const room = get().rooms[roomCode];
        if (!room) return [];

        const lowerQuery = query.toLowerCase();
        return room.transcriptions.filter(t =>
          t.transcript.toLowerCase().includes(lowerQuery) ||
          (t.translation && t.translation.toLowerCase().includes(lowerQuery)) ||
          t.userName.toLowerCase().includes(lowerQuery)
        );
      },

      /**
       * Get statistics for a room
       */
      getRoomStats: (roomCode) => {
        const room = get().rooms[roomCode];
        if (!room) return null;

        const transcriptions = room.transcriptions;
        const userCounts = {};

        transcriptions.forEach(t => {
          userCounts[t.userId] = (userCounts[t.userId] || 0) + 1;
        });

        return {
          total: transcriptions.length,
          users: Object.keys(userCounts).length,
          userBreakdown: userCounts,
          firstMessage: transcriptions[0]?.timestamp,
          lastMessage: transcriptions[transcriptions.length - 1]?.timestamp,
          lastUpdated: room.lastUpdated,
        };
      },

      /**
       * Update a specific transcription
       */
      updateTranscription: (roomCode, transcriptionId, updates) => {
        set((state) => {
          const room = state.rooms[roomCode];
          if (!room) return state;

          const updatedTranscriptions = room.transcriptions.map(t =>
            t.id === transcriptionId ? { ...t, ...updates } : t
          );

          return {
            rooms: {
              ...state.rooms,
              [roomCode]: {
                ...room,
                transcriptions: updatedTranscriptions,
                lastUpdated: Date.now(),
              },
            },
          };
        });
      },

      /**
       * Delete a specific transcription
       */
      deleteTranscription: (roomCode, transcriptionId) => {
        set((state) => {
          const room = state.rooms[roomCode];
          if (!room) return state;

          const filteredTranscriptions = room.transcriptions.filter(
            t => t.id !== transcriptionId
          );

          return {
            rooms: {
              ...state.rooms,
              [roomCode]: {
                ...room,
                transcriptions: filteredTranscriptions,
                lastUpdated: Date.now(),
              },
            },
          };
        });
      },
    }),
    {
      name: 'transcription-storage',
      version: 1,

      // Partialize: only persist rooms data
      partialize: (state) => ({ rooms: state.rooms }),

      // Migration function for version updates
      migrate: (persistedState, version) => {
        if (version === 0) {
          // Migration from v0 to v1
          return {
            rooms: persistedState.rooms || {},
          };
        }
        return persistedState;
      },

    }
  )
);

// Auto-cleanup on mount (runs once when store is initialized)
if (typeof window !== 'undefined') {
  // Cleanup old data on app start
  setTimeout(() => {
    useTranscriptionStore.getState().cleanup(7); // Keep 7 days
  }, 1000);
}

export default useTranscriptionStore;